import * as React from 'react';
import styled from '@emotion/styled';

import { Icon, type IconName } from './Icon';

export interface AccordionProps {
  /** Left icon name (DS icon). */
  icon?: IconName;
  title: React.ReactNode;
  /** Uncontrolled initial open state. Ignored if `open` is provided. */
  defaultOpen?: boolean;
  /** Controlled open state. */
  open?: boolean;
  onToggle?: (open: boolean) => void;
  children?: React.ReactNode;
  className?: string;
}

/**
 * Lumen accordion — Luminoso DS 2026 "Filter Accordion". A 54px header with
 * an optional leading icon, a title, and a chevron that rotates when open;
 * the body reveals below. Works controlled (`open` + `onToggle`) or
 * uncontrolled (`defaultOpen`).
 */
export function Accordion({
  icon,
  title,
  defaultOpen = false,
  open,
  onToggle,
  children,
  className
}: AccordionProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const toggle = () => {
    const next = !isOpen;
    if (!isControlled) setInternalOpen(next);
    onToggle?.(next);
  };

  return (
    <Root className={className}>
      <HeaderButton type="button" onClick={toggle} aria-expanded={isOpen}>
        <HeaderLeft>
          {icon && <Icon name={icon} size={20} />}
          <Title>{title}</Title>
        </HeaderLeft>
        <Chevron $open={isOpen}>
          <Icon name="chevron-down" size={14} />
        </Chevron>
      </HeaderButton>
      {isOpen && <Body>{children}</Body>}
    </Root>
  );
}

const Root = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.color.slate[100]};
`;

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  height: 54px;
  padding: 16px;
  background: none;
  border: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.color.fg.default};

  &:hover {
    background: ${({ theme }) => theme.color.bg.subtle};
  }
`;

const HeaderLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Title = styled.span`
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: ${({ theme }) => theme.color.fg.default};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.color.fg.muted};
  transition: transform 150ms ease;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

const Body = styled.div`
  padding: 0 16px 16px;
`;
